import { msg } from "@lingui/core/macro";
import { type Component, type JSX, Show } from "solid-js";
import { CloudOff, Lock, SearchX } from "lucide-solid";
import { useI18n } from "@/providers/i18n-provider";
import { EmptyState } from "./empty-state";
import { Button } from "./button";

type NotFoundVariant = "not-found" | "no-access" | "offline";

interface NotFoundStateProps {
  variant?: NotFoundVariant;
  /** Etiqueta de la entidad (p.ej. "recipe", "list") para personalizar el copy. */
  entityLabel?: string;
  /** Título y descripción a medida (sobreescriben el copy por defecto). */
  title?: string;
  description?: string;
  /** Acción primaria (p.ej. volver al inicio / a la lista padre). */
  primaryAction?: { label: string; onClick: () => void };
  /** Acción secundaria opcional (p.ej. reintentar). */
  secondaryAction?: { label: string; onClick: () => void };
  class?: string;
}

const ICONS: Record<NotFoundVariant, Component<{ class?: string }>> = {
  "not-found": SearchX,
  "no-access": Lock,
  offline: CloudOff,
};

/**
 * Estado de "no encontrado / sin acceso / sin conexión" para rutas de detalle
 * cuya entidad no se pudo resolver (ver `createEntityResolution`). Reutiliza el
 * `EmptyState` premium (ilustración brand-aware, animaciones bajo guard de
 * reduced-motion) y ofrece copy localizado por defecto + acciones claras, en
 * lugar de dejar la pantalla cargando indefinidamente.
 */
export const NotFoundState: Component<NotFoundStateProps> = (props) => {
  const { t } = useI18n();
  const variant = (): NotFoundVariant => props.variant ?? "not-found";

  const defaultTitle = (): string => {
    const v = variant();
    if (v === "no-access") {
      return t(msg`You don't have access`);
    }
    if (v === "offline") {
      return t(msg`Can't load this yet`);
    }
    return t(msg`Not found`);
  };

  const defaultDescription = (): string => {
    const v = variant();
    if (v === "no-access") {
      return t(msg`This item doesn't exist or you don't have permission to view it.`);
    }
    if (v === "offline") {
      return t(
        msg`You're offline and this hasn't synced to this device yet. Reconnect and try again.`,
      );
    }
    return t(msg`This item may have been deleted, or the link is no longer valid.`);
  };

  const action = (): JSX.Element => (
    <div class="flex flex-col items-center gap-2 sm:flex-row">
      <Show when={props.primaryAction}>
        {(primary) => (
          <Button
            onClick={() => {
              primary().onClick();
            }}
          >
            {primary().label}
          </Button>
        )}
      </Show>
      <Show when={props.secondaryAction}>
        {(secondary) => (
          <Button
            variant="outline"
            onClick={() => {
              secondary().onClick();
            }}
          >
            {secondary().label}
          </Button>
        )}
      </Show>
    </div>
  );

  // El CTA al que apunta el ícono: la acción primaria si existe, si no la
  // secundaria. Permite que tap/click en la ilustración dispare la misma
  // navegación/reintento que el botón (no callejón sin salida).
  const iconCta = (): { label: string; onClick: () => void } | undefined =>
    props.primaryAction ?? props.secondaryAction;

  return (
    <EmptyState
      icon={ICONS[variant()]}
      title={props.title ?? defaultTitle()}
      description={props.description ?? defaultDescription()}
      action={iconCta() ? action() : undefined}
      onIconActivate={iconCta() ? () => iconCta()?.onClick() : undefined}
      iconActionLabel={iconCta()?.label}
      class={props.class}
    />
  );
};
